import {
  GetItemReportDetailsRequest,
  GetItemReportDetailsResponse,
  GetItemsReportRequest,
  GetItemsReportResponse,
} from '@classpass/protobuf-definitions/lib/web/models/partners/sku_order';
import { useQuery } from '@tanstack/react-query';

import URLS from 'constants/urls';
import { transformKeysToCamelCase } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';

import { normalizeItemReportDetails, normalizeItemsReport } from './SkuItemReports.normalizers';
import { DATE_RANGE_PRESET, presetRange, toPickupTimeRange } from './SkuOrderReports.filters';

export const skuItemReportsKeys = {
  all: ['skuItemReports'],
  items: filter => [...skuItemReportsKeys.all, 'items', filter],
  details: (itemId, range) => [...skuItemReportsKeys.all, 'details', itemId, range],
};

async function fetchItemsReport(filter) {
  try {
    const response = await httpClient.post(URLS.reports.skuItems, GetItemsReportRequest.toJSON(filter), {
      ignoreInternalError: true,
    });
    return transformKeysToCamelCase(GetItemsReportResponse.fromJSON(response?.data || {}));
  } catch (error) {
    reportError(error);
    throw error;
  }
}

export function useItemsReportQuery(filter) {
  return useQuery({
    queryKey: skuItemReportsKeys.items(filter),
    queryFn: () => fetchItemsReport(filter),
    enabled: Boolean(filter?.venue_ids?.length),
    select: normalizeItemsReport,
  });
}

async function fetchItemReportDetails(itemId, requestedPickupTimeRange) {
  try {
    const response = await httpClient.post(
      URLS.reports.skuItemDetails(itemId),
      GetItemReportDetailsRequest.toJSON({ requested_pickup_time_range: requestedPickupTimeRange }),
      { ignoreInternalError: true }
    );
    return transformKeysToCamelCase(GetItemReportDetailsResponse.fromJSON(response?.data || {}));
  } catch (error) {
    reportError(error);
    throw error;
  }
}

export function useItemReportDetailsQuery(itemId) {
  // The flyout window is fixed to the last 7 closed days regardless of the page's selection.
  const requestedPickupTimeRange = toPickupTimeRange(presetRange(DATE_RANGE_PRESET.LAST_7_DAYS));
  return useQuery({
    queryKey: skuItemReportsKeys.details(itemId, requestedPickupTimeRange),
    queryFn: () => fetchItemReportDetails(itemId, requestedPickupTimeRange),
    enabled: Boolean(itemId),
    select: normalizeItemReportDetails,
  });
}
